import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { AssetConstants } from 'src/constants';
import { AssetState, AssetStatus } from 'src/types';

import { CropOrientation } from './CropModal';
import { toDisplayCoords, toOriginalCoords } from './cropUtils';

interface UseCropLogicProps {
  isVisible: boolean;
  imageState: AssetState;
  orientation: CropOrientation;
  /** Override the kept size (slot width for horizontal, slot height for vertical) */
  keptSize?: number;
  /** Override the final output size (canvas width for horizontal, canvas height for vertical) */
  outputSize?: number;
}

interface UseCropLogicReturn {
  cropBoxPosition: number;
  cropBoxSize: number;
  imageRef: React.RefObject<HTMLImageElement>;
  handleImageLoad: () => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleSave: (
    updateAssetState: (state: Partial<AssetState>) => void,
    onClose?: () => void,
  ) => void;
}

export const useCropLogic = ({
  isVisible,
  imageState,
  orientation,
  keptSize: keptSizeOverride,
  outputSize: outputSizeOverride,
}: UseCropLogicProps): UseCropLogicReturn => {
  const isHorizontal = orientation === 'horizontal';
  const [cropBoxPosition, setCropBoxPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [imageDimension, setImageDimension] = useState(0);
  const imageRef = useRef<HTMLImageElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();
  const cropBoxPositionRef = useRef(cropBoxPosition);
  const imageDimensionRef = useRef(imageDimension);

  const keptSize =
    keptSizeOverride ??
    (isHorizontal ? AssetConstants.CROP.KEPT_WIDTH : AssetConstants.CROP.KEPT_HEIGHT);
  const finalOutputSize =
    outputSizeOverride ??
    (isHorizontal
      ? AssetConstants.CROP.FINAL_OUTPUT_WIDTH
      : AssetConstants.CROP.FINAL_OUTPUT_HEIGHT);

  // Keep refs in sync with state
  useEffect(() => {
    cropBoxPositionRef.current = cropBoxPosition;
    imageDimensionRef.current = imageDimension;
  }, [cropBoxPosition, imageDimension]);

  // Memoized calculations using current DOM measurements
  const { cropBoxSize, maxCropPosition } = useMemo(() => {
    if (imageDimension === 0) {
      return { cropBoxSize: 0, maxCropPosition: 0 };
    }

    const scaleFactor = imageDimension / finalOutputSize;
    const cropBoxSize = keptSize * scaleFactor;
    const maxCropPosition = imageDimension - cropBoxSize;

    return { cropBoxSize, maxCropPosition };
  }, [imageDimension, finalOutputSize, keptSize]);

  // Initialize crop position when modal opens
  useEffect(() => {
    if (!isVisible) {
      setIsDragging(false);
      setCropBoxPosition(0);
      setImageDimension(0);
      return;
    }

    const existingOffset = isHorizontal ? imageState?.leftOffset : imageState?.topOffset;
    if (imageDimension > 0 && existingOffset !== undefined && imageRef.current) {
      const naturalDim = isHorizontal
        ? imageRef.current.naturalWidth
        : imageRef.current.naturalHeight;
      const clientDim = isHorizontal ? imageRef.current.clientWidth : imageRef.current.clientHeight;
      const displayPosition = toDisplayCoords(existingOffset, naturalDim, clientDim);
      setCropBoxPosition(displayPosition);
    }
  }, [isVisible, imageDimension, isHorizontal, imageState?.topOffset, imageState?.leftOffset]);

  // Resize handler - recreates when orientation or crop dimensions change
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      if (!imageRef.current) return;

      const oldDimension = imageDimensionRef.current;
      const newDimension = isHorizontal
        ? imageRef.current.clientWidth
        : imageRef.current.clientHeight;

      if (oldDimension === newDimension) return;

      const relativePosition = oldDimension > 0 ? cropBoxPositionRef.current / oldDimension : 0;
      const scaleFactor = newDimension / finalOutputSize;
      const newCropBoxSize = keptSize * scaleFactor;
      const maxPos = newDimension - newCropBoxSize;
      const newCropPosition = Math.max(0, Math.min(relativePosition * newDimension, maxPos));

      setImageDimension(newDimension);
      setCropBoxPosition(newCropPosition);
    }, AssetConstants.CROP.RESIZE_DEBOUNCE_MS);
  }, [isHorizontal, finalOutputSize, keptSize]);

  // Handle window resize and image resize
  useEffect(() => {
    if (!isVisible) return;

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    let resizeObserver: ResizeObserver | null = null;
    if (imageRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(imageRef.current);
    }

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      resizeObserver?.disconnect();
    };
  }, [isVisible, handleResize]);

  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // Mouse and touch move/end handlers
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!imageRef.current) return;
      e.preventDefault();

      const rect = imageRef.current.getBoundingClientRect();
      const newPosition = isHorizontal
        ? Math.max(0, Math.min(e.clientX - rect.left, maxCropPosition))
        : Math.max(0, Math.min(e.clientY - rect.top, maxCropPosition));
      setCropBoxPosition(newPosition);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!imageRef.current) return;
      e.preventDefault();

      const rect = imageRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      const newPosition = isHorizontal
        ? Math.max(0, Math.min(touch.clientX - rect.left, maxCropPosition))
        : Math.max(0, Math.min(touch.clientY - rect.top, maxCropPosition));
      setCropBoxPosition(newPosition);
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
    document.body.style.userSelect = 'none';
    document.body.style.cursor = isHorizontal ? 'ew-resize' : 'move';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, maxCropPosition, isHorizontal]);

  // Image handlers
  const handleImageLoad = () => {
    if (imageRef.current) {
      setImageDimension(
        isHorizontal ? imageRef.current.clientWidth : imageRef.current.clientHeight,
      );
    }
  };

  const handleSave = (
    updateAssetState: (state: Partial<AssetState>) => void,
    onClose?: () => void,
  ) => {
    if (!imageRef.current) return;

    const naturalDim = isHorizontal
      ? imageRef.current.naturalWidth
      : imageRef.current.naturalHeight;
    const clientDim = isHorizontal ? imageRef.current.clientWidth : imageRef.current.clientHeight;
    const offsetInOriginalCoords = toOriginalCoords(cropBoxPosition, naturalDim, clientDim);

    const stateUpdate: Partial<AssetState> = {
      status: AssetStatus.PENDING_TRANSFORMATION,
      ...(isHorizontal
        ? { leftOffset: offsetInOriginalCoords }
        : { topOffset: offsetInOriginalCoords }),
    };

    updateAssetState(stateUpdate);
    onClose?.();
  };

  return {
    cropBoxPosition,
    cropBoxSize,
    imageRef,
    handleImageLoad,
    handleMouseDown,
    handleTouchStart,
    handleSave,
  };
};
